import type { PlayerFinderTimeOfDayEnum } from 'types/generated/client';
import {
  AVAILABILITY_DAY_OPTIONS,
  type AvailabilityDay,
  TIME_OF_DAY_OPTIONS,
  type TimesByDay,
} from 'utils/shared/playerAvailability';
import Pill from 'components/Pill';

export interface AvailabilitySelection {
  days: AvailabilityDay[];
  timesByDay: TimesByDay;
}

interface SelectionProps extends AvailabilitySelection {
  onChange: (next: AvailabilitySelection) => void;
}

/**
 * Day/time selection logic for "when are you usually available". Days and time
 * buckets are coarse on purpose: this is a standing habit, not the exact HH:mm
 * slots a listing carries. The pieces are exported separately because onboarding
 * lays each day out as its own labelled row while the profile editor stacks them.
 */
export function useAvailabilitySelection({ days, timesByDay, onChange }: SelectionProps) {
  const toggleDay = (day: AvailabilityDay) => {
    if (!days.includes(day)) {
      onChange({ days: [...days, day], timesByDay });
      return;
    }

    // Drop the day's time buckets with it, so they can't resurface unseen.
    const nextTimesByDay = { ...timesByDay };
    delete nextTimesByDay[day];
    onChange({ days: days.filter((entry) => entry !== day), timesByDay: nextTimesByDay });
  };

  const toggleTimeOfDay = (day: AvailabilityDay, time: PlayerFinderTimeOfDayEnum) => {
    const current = timesByDay[day] ?? [];
    const next = current.includes(time)
      ? current.filter((entry) => entry !== time)
      : [...current, time];
    onChange({ days, timesByDay: { ...timesByDay, [day]: next } });
  };

  // Chip order rather than tap order, so the sections don't reshuffle as you select.
  const selectedDayOptions = AVAILABILITY_DAY_OPTIONS.filter((option) =>
    days.includes(option.value),
  );

  return { toggleDay, toggleTimeOfDay, selectedDayOptions };
}

export function AvailabilityDayPills({
  days,
  onToggle,
}: {
  days: AvailabilityDay[];
  onToggle: (day: AvailabilityDay) => void;
}) {
  return (
    <div className="flex flex-wrap gap-ds-md">
      {AVAILABILITY_DAY_OPTIONS.map((option) => (
        <Pill
          key={option.value}
          mode="select"
          text={option.label}
          selected={days.includes(option.value)}
          onClick={() => onToggle(option.value)}
        />
      ))}
    </div>
  );
}

export function AvailabilityTimePills({
  times,
  onToggle,
}: {
  times: PlayerFinderTimeOfDayEnum[];
  onToggle: (time: PlayerFinderTimeOfDayEnum) => void;
}) {
  return (
    <div className="flex flex-wrap gap-ds-md">
      {TIME_OF_DAY_OPTIONS.map((time) => (
        <Pill
          key={time.value}
          mode="select"
          text={time.label}
          selected={times.includes(time.value)}
          onClick={() => onToggle(time.value)}
        />
      ))}
    </div>
  );
}

/** The stacked composition, for surfaces that don't need a two-column label. */
export default function AvailabilityField({ days, timesByDay, onChange }: SelectionProps) {
  const { toggleDay, toggleTimeOfDay, selectedDayOptions } = useAvailabilitySelection({
    days,
    timesByDay,
    onChange,
  });

  return (
    <div className="flex flex-col gap-ds-xl">
      <AvailabilityDayPills days={days} onToggle={toggleDay} />

      {selectedDayOptions.map((option) => (
        <div key={option.value} className="flex flex-col gap-ds-lg">
          <p className="typography-product-subheading text-color-text-lightmode-secondary dark:text-color-text-darkmode-secondary">
            {option.label}:
          </p>
          <AvailabilityTimePills
            times={timesByDay[option.value] ?? []}
            onToggle={(time) => toggleTimeOfDay(option.value, time)}
          />
        </div>
      ))}
    </div>
  );
}
